import React, { useMemo, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import Main from '../Main';
import { useRecordFit } from './useRecordFit';
import '../../styles/medicalRecordContent.css';

// Cooperation.Odoro - the record page is always the Odoro flavour of the header: the icon
// beside the doctor's name, and the wordmark for a clinic with no logo of its own.
const ODORO = 1;

// Tooltip and accessible name for the action buttons - their only content is an icon.
const ALT = {
    print: 'הדפסה',
    save: 'שמירה'
};

const printIcon = (
    <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path fill="currentColor" d="M19 8H5c-1.66 0-3 1.34-3 3v6h4v4h12v-4h4v-6c0-1.66-1.34-3-3-3zm-3 11H8v-5h8v5zm3-7c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zM18 3H6v4h12V3z" />
    </svg>
);

const saveIcon = (
    <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path fill="currentColor" d="M5 20h14v-2H5v2zM19 9h-4V3H9v6H5l7 7 7-7z" />
    </svg>
);

// CAL4 writes absolute image urls into the record body. They display fine, but the pdf is drawn
// by html2canvas, which reads the pixels back out of a canvas - and a cross origin image whose
// host sends no CORS headers cannot be read, so it comes out blank. Fetching it through our own
// backend instead makes it same origin, where that restriction does not apply.
const IMAGE_PROXY = '/medicalrecord/image?url=';

const absolute = (src) => new URL(src, window.location.href);

/// data urls are already local; anything else off this origin has to go through the proxy.
const needsProxy = (img) => {
    const src = img.getAttribute('src');
    if (!src || src.startsWith('data:')) {
        return false;
    }

    try {
        return absolute(src).origin !== window.location.origin;
    } catch {
        // Not a url we can rewrite - leave it exactly as the record had it.
        return false;
    }
}

/// A copy of the record, toolbar removed, with its images pointed at the proxy,
/// mounted off screen at the width of the original so the capture lays out the way the page does.
/// The copy keeps its classes, so both record stylesheets still apply, and inherits dir=rtl from
/// <html>.
const mountPrintable = (source) => {
    const printable = source.cloneNode(true);

    // On screen furniture: the patient is not downloading a picture of the buttons.
    printable.querySelectorAll('.recordActions').forEach(actions => actions.remove());

    // On a narrow screen the record is drawn scaled down to the width of the phone - see
    // useRecordFit. The pdf wants the document at its own size, so drop the scale the copy
    // carries. Everything else that layout adds is scoped to .recordPage, which this copy is
    // not inside.
    printable.querySelectorAll('.recordStage, .recordFitSizer')
        .forEach(element => element.removeAttribute('style'));

    printable.querySelectorAll('img').forEach(img => {
        if (!needsProxy(img)) {
            return;
        }

        // srcset would let the browser pick a remote candidate over the proxied src.
        img.removeAttribute('srcset');
        img.setAttribute('src', `${IMAGE_PROXY}${encodeURIComponent(absolute(img.getAttribute('src')).href)}`);
    });

    // Laid out at the width of the document rather than the width of the screen, so a record
    // saved on a phone comes out as the same page it does on a desktop.
    const width = Math.max(source.offsetWidth, source.querySelector('.recordStage')?.offsetWidth ?? 0);

    const host = document.createElement('div');
    host.setAttribute('aria-hidden', 'true');
    host.style.cssText = `position:fixed;top:0;left:-10000px;width:${width}px;background:#fff`;
    host.appendChild(printable);
    document.body.appendChild(host);

    return { host, printable };
}

/// html2canvas draws whatever has decoded by the time it runs, so the swapped images have to
/// finish first - including the ones that fail, which simply stay blank as they would anyway.
const settled = (host) => Promise.all(
    Array.from(host.querySelectorAll('img')).map(img => img.complete
        ? Promise.resolve()
        : new Promise(resolve => {
            img.addEventListener('load', resolve, { once: true });
            img.addEventListener('error', resolve, { once: true });
        })));

/// html2pdf is a megabyte and a half and only the save button needs it, so it arrives as its
/// own chunk. A chunk that fails to download throws ChunkLoadError - a request dropped on a
/// slow connection, or a dev server mid rebuild after a hot reload. webpack forgets a chunk
/// whose load failed, so importing it again really does retry the download.
const loadHtml2Pdf = async () => {
    // The package main is the unminified bundle, whose inlined deps point at source maps it
    // does not ship (a build warning per dep). The minified build ships its map.
    const load = () => import('html2pdf.js/dist/html2pdf.min.js');

    try {
        return (await load()).default;
    } catch {
        return (await load()).default;
    }
}

export const RecordView = ({ record, clinicLogoUrl }) => {
    const printableRef = useRef(null);
    const { frameRef, sizerRef, stageRef } = useRecordFit();
    const [saving, setSaving] = useState(false);

    // The record body is rendered html produced by CAL4; sanitize before injecting it.
    // Embedded base64 signatures and logos survive: img is in DOMPurify's data-uri tags.
    const html = useMemo(() => DOMPurify.sanitize(record?.html ?? ""), [record]);

    const print = () => window.print();

    const save = async () => {
        if (saving) {
            return;
        }

        setSaving(true);

        // The proxied copy is captured instead of the live record, so the page the patient is
        // looking at never flickers through a reload of every image.
        const { host, printable } = mountPrintable(printableRef.current);
        try {
            await settled(host);

            const html2pdf = await loadHtml2Pdf();
            await html2pdf().set({
                filename: `${record.fileName ?? 'medical-record'}.pdf`,
                margin: 10,
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { scale: 2, useCORS: true },
                jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
            }).from(printable).save();
        } finally {
            host.remove();
            setSaving(false);
        }
    }

    return (
        <Main
            appointment={{ doctorName: record?.doctorName, cooperation: ODORO }}
            title={'רשומה רפואית'}
            className={"recordPage"}
            logo={clinicLogoUrl
                ? <img className={"recordHeaderLogo"} src={clinicLogoUrl} alt={'לוגו המרפאה'} />
                : null}>
            <div className={"container recordContainer"}>
                {/* What the pdf captures. The toolbar is inside it purely to keep the on
                    screen order, and is dropped from the copy that gets captured - see
                    mountPrintable. */}
                <div className={"recordPrintable"} ref={printableRef}>
                    <div className={"recordDocument"}>
                        <div className={"recordActions"} aria-busy={saving}>
                            <button
                                type={"button"}
                                className={"recordActionButton"}
                                onClick={print}
                                title={ALT.print}
                                aria-label={ALT.print}>
                                {printIcon}
                            </button>
                            <button
                                type={"button"}
                                className={"recordActionButton"}
                                onClick={save}
                                title={ALT.save}
                                aria-label={ALT.save}>
                                {saveIcon}
                            </button>
                        </div>
                        {/* The record scaled to the screen on a narrow one: the stage holds
                            the document at its own width, the sizer carries the scaled box it
                            takes up in the page. Plain nesting on a wide one. */}
                        <div className={"recordFrame"} ref={frameRef}>
                            <div className={"recordFitSizer"} ref={sizerRef}>
                                <div className={"recordStage"} ref={stageRef}>
                                    <div className={"recordContent"} dangerouslySetInnerHTML={{ __html: html }} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Main>
    );
}
